import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_DOMAIN = 'https://nolahvacconnect.com';

const PAGE_META = {
  '/': {
    title: 'NOLA HVAC Connect | Trusted HVAC Professionals in New Orleans',
    description: 'Connect with licensed HVAC professionals in New Orleans, Metairie, Kenner, Slidell, and the Northshore. Free estimates for AC repair, installation, and replacement.',
  },
  '/instant-hvac-estimate-new-orleans': {
    title: 'Instant HVAC Estimate New Orleans | Free Online AC Replacement Cost',
    description: 'Get an instant HVAC replacement estimate for your New Orleans area home. Free, no phone call required. See pricing for AC installation and system replacement.',
  },
  '/hvac-pricing-new-orleans': {
    title: 'HVAC Pricing New Orleans | AC Installation & Replacement Costs 2026',
    description: 'See current HVAC pricing for New Orleans homeowners. Compare AC replacement costs, installation pricing, and repair estimates for Southeast Louisiana.',
  },
  '/ac-replacement-cost-new-orleans': {
    title: 'AC Replacement Cost New Orleans | What to Expect in 2026',
    description: 'Learn what AC replacement costs in New Orleans. Understand pricing factors for central AC, heat pumps, and high-efficiency systems in Southeast Louisiana.',
  },
  '/check-ac-age': {
    title: 'How Old Is My AC Unit? | Decode Your Serial Number',
    description: 'Find out how old your air conditioner is by decoding the serial number. Free AC age checker guide for Louisiana homeowners.',
  },
  '/ac-unit-lifespan-louisiana': {
    title: 'How Long Do AC Units Last in Louisiana? | AC Lifespan Guide',
    description: 'Learn how long AC units last in Louisiana\'s heat and humidity. Average AC lifespan, signs of failure, and when to consider replacement.',
  },
  '/repair-vs-replace-ac': {
    title: 'Repair vs Replace AC | When to Replace Your Air Conditioner',
    description: 'Should you repair or replace your air conditioner? Use our guide to evaluate system age, repair costs, and efficiency to make the right decision.',
  },
  '/common-ac-problems': {
    title: 'Common AC Problems | Troubleshooting Guide for Louisiana Homeowners',
    description: 'Diagnose common air conditioner problems. Learn about AC issues like warm air, freezing, leaks, and strange noises with our Louisiana homeowner guide.',
  },
  '/ac-blowing-warm-air': {
    title: 'AC Blowing Warm Air? | Causes & Fixes for Louisiana Homeowners',
    description: 'Why is your AC blowing warm air? Common causes include low refrigerant, dirty filters, and compressor issues. Learn what to check before calling a technician.',
  },
  '/ac-freezing-up': {
    title: 'AC Freezing Up? | Why Your AC Unit Is Icing Over',
    description: 'Learn why your AC unit is freezing up and what causes ice buildup on your air conditioner. Troubleshooting guide for Louisiana homeowners.',
  },
  '/ac-running-but-not-cooling': {
    title: 'AC Running But Not Cooling? | Troubleshooting Guide',
    description: 'Your AC is running but not cooling your home. Learn the most common causes and whether you need a repair or replacement.',
  },
  '/ac-leaking-water': {
    title: 'AC Leaking Water? | Common Causes & What to Do',
    description: 'Find out why your air conditioner is leaking water. Common causes include clogged drain lines, frozen coils, and installation issues.',
  },
  '/ac-not-turning-on': {
    title: 'AC Not Turning On? | Troubleshooting Steps for Homeowners',
    description: 'Your AC won\'t turn on. Check these common causes including thermostat issues, tripped breakers, and capacitor failures before calling for service.',
  },
  '/ac-making-loud-noise': {
    title: 'AC Making Loud Noise? | What Different AC Sounds Mean',
    description: 'Strange noises from your AC? Learn what banging, screeching, buzzing, and clicking sounds mean and when they indicate a serious problem.',
  },
  '/hvac-refrigerant-guide': {
    title: 'HVAC Refrigerant Guide | R-22 vs R-410A vs New Refrigerants',
    description: 'Understand HVAC refrigerants including R-22, R-410A, and new A2L types. Learn how refrigerant type affects repair and replacement decisions.',
  },
  '/compare-hvac-contractors': {
    title: 'How to Compare HVAC Contractors | Southeast Louisiana Guide',
    description: 'Learn what to look for when comparing HVAC contractors in New Orleans and the Northshore. Licensing, warranties, pricing, and warning signs to watch for.',
  },
  '/how-nola-hvac-connect-works': {
    title: 'How NOLA HVAC Connect Works | Homeowner HVAC Resource',
    description: 'Learn how NOLA HVAC Connect helps homeowners understand HVAC options, compare pricing, and connect with trusted professionals in Southeast Louisiana.',
  },
  '/who-makes-hvac-systems': {
    title: 'Who Makes HVAC Systems? | Brand Ownership Guide 2026',
    description: 'Discover which companies manufacture popular HVAC brands. Daikin, Carrier, Trane, Rheem, and more — learn the parent companies behind your AC unit.',
  },
  '/what-size-ac-unit-do-i-need': {
    title: 'What Size AC Unit Do I Need? | HVAC Sizing Guide for Louisiana',
    description: 'Learn how to determine the right AC unit size for your Louisiana home. Understand tonnage, square footage, and factors that affect HVAC sizing.',
  },
  '/contractors': {
    title: 'HVAC Contractor Leads | Join NOLA HVAC Connect',
    description: 'Grow your HVAC business with qualified leads from homeowners in the Greater New Orleans area. Pay-per-lead model with no contracts.',
  },
};

const CITY_META = {
  'new-orleans': { title: 'HVAC Services New Orleans LA | AC Repair & Installation', description: 'Find trusted HVAC professionals in New Orleans. AC repair, installation, and replacement services for the French Quarter, Garden District, Uptown, and all neighborhoods.' },
  'metairie': { title: 'HVAC Services Metairie LA | AC Repair & Replacement', description: 'Connect with licensed HVAC contractors in Metairie. AC repair, heating installation, and system replacement for Jefferson Parish homeowners.' },
  'kenner': { title: 'HVAC Services Kenner LA | AC Repair & Installation', description: 'Trusted HVAC professionals serving Kenner and surrounding areas. AC repair, replacement, and maintenance services.' },
  'slidell': { title: 'HVAC Services Slidell LA | AC Repair & Replacement', description: 'Licensed HVAC contractors serving Slidell and the Northshore. AC repair, installation, and system replacement services.' },
  'covington': { title: 'HVAC Services Covington LA | AC Repair & Installation', description: 'Connect with trusted HVAC professionals in Covington. AC repair, heating, and full system replacement for St. Tammany Parish.' },
  'mandeville': { title: 'HVAC Services Mandeville LA | AC Repair & Replacement', description: 'Find licensed HVAC contractors serving Mandeville. AC repair, installation, and maintenance for Northshore homeowners.' },
  'hammond': { title: 'HVAC Services Hammond LA | AC Repair & Installation', description: 'Trusted HVAC professionals in Hammond. AC repair, replacement, and maintenance for Tangipahoa Parish homeowners.' },
  'gretna': { title: 'HVAC Services Gretna LA | AC Repair & Replacement', description: 'Licensed HVAC contractors serving Gretna and the West Bank. AC repair, installation, and system replacement.' },
  'marrero': { title: 'HVAC Services Marrero LA | AC Repair & Installation', description: 'Connect with HVAC professionals in Marrero. AC repair, heating, and replacement services for West Bank homeowners.' },
  'chalmette': { title: 'HVAC Services Chalmette LA | AC Repair & Replacement', description: 'Find trusted HVAC contractors in Chalmette. AC repair, installation, and maintenance for St. Bernard Parish.' },
};

/**
 * SEO component — adds canonical, meta title/description, and breadcrumb schema.
 * Drop into any page: <PageSEO breadcrumbs={[{name:'Home', url:'/'}, {name:'Page', url:'/page'}]} />
 */
const PageSEO = ({ breadcrumbs = [], citySlug = null }) => {
  const location = useLocation();
  const path = location.pathname;
  const canonicalUrl = `${CANONICAL_DOMAIN}${path === '/' ? '' : path}`;

  useEffect(() => {
    const meta = citySlug
      ? CITY_META[citySlug] || {}
      : PAGE_META[path] || {};

    // Set canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonicalUrl;

    // Set title
    if (meta.title) document.title = meta.title;

    // Set meta description
    let desc = document.querySelector('meta[name="description"]');
    if (desc && meta.description) desc.content = meta.description;

    // Set OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && meta.title) ogTitle.content = meta.title;
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && meta.description) ogDesc.content = meta.description;
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = canonicalUrl;

    // Add breadcrumb schema
    if (breadcrumbs.length > 0) {
      const existingBreadcrumb = document.querySelector('script[data-seo="breadcrumb"]');
      if (existingBreadcrumb) existingBreadcrumb.remove();

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'breadcrumb');
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((bc, idx) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "name": bc.name,
          "item": `${CANONICAL_DOMAIN}${bc.url}`,
        })),
      });
      document.head.appendChild(script);
    }

    return () => {
      const breadcrumbScript = document.querySelector('script[data-seo="breadcrumb"]');
      if (breadcrumbScript) breadcrumbScript.remove();
    };
  }, [canonicalUrl, path, citySlug, breadcrumbs]);

  return null;
};

export default PageSEO;
export { CANONICAL_DOMAIN, PAGE_META, CITY_META };
